import React, { PureComponent } from "react";
import { getTtsAudio } from "@s/api/file";
import PronunciationApi from "@s/api/pronunciation";
import {
  ReadAloudContainer,
  ReadAloudIconButton,
  ReadAloudVoiceSelect,
} from "./ReadAloudButton.styled";

// Shared across every ReadAloudButton instance on a page so mounting many
// (e.g. one per quiz option) triggers a single voices request, not one each.
let voicesCache = null;
let voicesPromise = null;

const fetchVoices = () => {
  if (voicesCache) return Promise.resolve(voicesCache);
  if (!voicesPromise) {
    voicesPromise = PronunciationApi.listVoices()
      .then(res => {
        voicesCache = (res && res.voices) || [];
        return voicesCache;
      })
      .catch(() => {
        voicesCache = [];
        return voicesCache;
      });
  }
  return voicesPromise;
};

/**
 * Lets an admin pick a TTS voice and hear how a piece of text (news
 * description, quiz option, etc.) will sound to a child, without leaving
 * the form.
 */
class ReadAloudButton extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      voices: voicesCache || [],
      selectedVoice: "",
      playing: false,
    };
    this.audio = null;
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    if (!voicesCache) {
      fetchVoices().then(voices => {
        if (this._isMounted) this.setState({ voices });
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.text !== this.props.text) {
      this.resetAudio();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.resetAudio();
  }

  resetAudio = () => {
    if (this.audio) {
      this.audio.pause();
      this.audio.removeEventListener("ended", this.handleAudioEnded);
      this.audio = null;
    }
    if (this._isMounted) this.setState({ playing: false });
  };

  handleAudioEnded = () => {
    if (this._isMounted) this.setState({ playing: false });
  };

  handleVoiceChange = e => {
    e.stopPropagation();
    this.resetAudio();
    this.setState({ selectedVoice: e.target.value });
  };

  handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    const text = (this.props.text || "").trim();
    if (!text || this.state.loading) return;

    // Already have audio for the current text/voice: just toggle play/pause
    // instead of re-fetching from the TTS service.
    if (this.audio && !this.audio.ended) {
      if (this.state.playing) {
        this.audio.pause();
        this.setState({ playing: false });
      } else {
        this.audio.play();
        this.setState({ playing: true });
      }
      return;
    }

    this.setState({ loading: true });
    getTtsAudio(text, this.state.selectedVoice || undefined)
      .then(res => {
        const audioBase64 = res && res.result && res.result.audio_base64;
        if (audioBase64) {
          this.audio = new Audio("data:audio/mp3;base64," + audioBase64);
          this.audio.addEventListener("ended", this.handleAudioEnded);
          this.audio.play();
          if (this._isMounted) this.setState({ playing: true });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (this._isMounted) this.setState({ loading: false });
      });
  };

  render() {
    const { text, title, style } = this.props;
    const { loading, voices, selectedVoice, playing } = this.state;
    const disabled = !text || !text.trim();
    const icon = loading
      ? "fa fa-spinner fa-spin"
      : playing
      ? "fa fa-pause"
      : "fa fa-volume-up";
    const buttonTitle = playing ? "Pause" : title || "Read aloud";

    return (
      <ReadAloudContainer style={style}>
        <ReadAloudVoiceSelect
          value={selectedVoice}
          onChange={this.handleVoiceChange}
          onClick={e => e.stopPropagation()}
          disabled={voices.length === 0}
          title="Voice"
        >
          <option value="">Default</option>
          {voices.map(v => (
            <option key={v.key} value={v.key}>
              {v.name}
            </option>
          ))}
        </ReadAloudVoiceSelect>
        <ReadAloudIconButton
          type="button"
          disabled={disabled}
          active={loading || playing}
          title={buttonTitle}
          onClick={this.handleClick}
        >
          <i className={icon} />
        </ReadAloudIconButton>
      </ReadAloudContainer>
    );
  }
}

export default ReadAloudButton;
